/**
 * Icon shim — every icon name the codebase used to import from `lucide-react`
 * is re-exported here, but the underlying SVG is a Phosphor icon with the
 * `regular` no-fill weight as the default. Override via the `weight` prop on
 * individual usages.
 *
 * Lucide sized via `className="h-4 w-4"`; Phosphor honors className/style
 * sizing as well, so existing call sites work without changes.
 *
 *   import { Plus, Settings } from '@/components/icons';
 *   <Plus className="h-4 w-4" />
 */
import { forwardRef } from 'react';
import type { Icon as PhosphorIcon, IconProps as PhosphorIconProps } from '@phosphor-icons/react';
import * as PI from '@phosphor-icons/react';

const DEFAULT_WEIGHT: PhosphorIconProps['weight'] = 'regular';

function wrap(Icon: PhosphorIcon | undefined, fallbackWeight?: PhosphorIconProps['weight']) {
  // Fallback if a Phosphor name doesn't exist at runtime — render a Circle so
  // the app keeps working instead of crashing on module load. Warn so the
  // mismatch is visible during development.
  const Resolved = (Icon ?? PI.Circle) as PhosphorIcon;
  if (!Icon && typeof console !== 'undefined') {
    console.warn('[icons] Missing Phosphor icon — falling back to Circle');
  }
  const Wrapped = forwardRef<SVGSVGElement, PhosphorIconProps>(({ weight, ...rest }, ref) => (
    <Resolved ref={ref} weight={weight ?? fallbackWeight ?? DEFAULT_WEIGHT} {...rest} />
  ));
  Wrapped.displayName = `Icon(${Resolved.displayName ?? 'Phosphor'})`;
  return Wrapped;
}

// ---- Alerts / status ----
export const AlertCircle    = wrap(PI.WarningCircle);
export const AlertTriangle  = wrap(PI.Warning);
export const Check          = wrap(PI.Check, 'bold');
export const CheckCircle    = wrap(PI.CheckCircle);
export const CheckCircle2   = wrap(PI.CheckCircle);
export const XCircle        = wrap(PI.XCircle);
export const Info           = wrap(PI.Info);

// ---- Arrows ----
export const ArrowDown        = wrap(PI.ArrowDown, 'regular');
export const ArrowDownRight   = wrap(PI.ArrowDownRight, 'regular');
export const ArrowDownToLine  = wrap(PI.ArrowLineDown, 'regular');
export const ArrowLeft        = wrap(PI.ArrowLeft, 'regular');
export const ArrowRight       = wrap(PI.ArrowRight, 'regular');
export const ArrowUp          = wrap(PI.ArrowUp, 'regular');
export const ArrowUpDown      = wrap(PI.ArrowsDownUp, 'regular');
export const ArrowUpRight     = wrap(PI.ArrowUpRight, 'regular');
export const ArrowUpToLine    = wrap(PI.ArrowLineUp, 'regular');
export const ChevronDown      = wrap(PI.CaretDown, 'bold');
export const ChevronLeft      = wrap(PI.CaretLeft, 'bold');
export const ChevronRight     = wrap(PI.CaretRight, 'bold');
export const ChevronUp        = wrap(PI.CaretUp, 'bold');
export const ChevronsUpDown   = wrap(PI.CaretUpDown, 'bold');
export const Undo             = wrap(PI.ArrowArcLeft, 'regular');
export const RefreshCw        = wrap(PI.ArrowsClockwise, 'regular');
export const RotateCcw        = wrap(PI.ArrowCounterClockwise, 'regular');
export const Maximize2        = wrap(PI.ArrowsOut, 'regular');
export const Move             = wrap(PI.ArrowsOutCardinal, 'regular');
export const ZoomIn           = wrap(PI.MagnifyingGlassPlus, 'regular');
export const ZoomOut          = wrap(PI.MagnifyingGlassMinus, 'regular');

// ---- People / orgs ----
export const User       = wrap(PI.User);
export const Users      = wrap(PI.Users);
export const UserPlus   = wrap(PI.UserPlus);
export const UserCog    = wrap(PI.UserGear);
export const HardHat    = wrap(PI.HardHat);
export const Crown      = wrap(PI.Crown);
export const Shield     = wrap(PI.Shield);
export const ShieldCheck = wrap(PI.ShieldCheck);
export const Building2  = wrap(PI.Buildings);
export const Warehouse  = wrap(PI.Warehouse);

// ---- Files / documents ----
export const FileIcon         = wrap(PI.File);
export const FileImage        = wrap(PI.FileImage);
export const FileSpreadsheet  = wrap(PI.Table);
export const FileTemplate     = wrap(PI.FileText);
export const FileText         = wrap(PI.FileText);
export const Bookmark         = wrap(PI.Bookmark);
export const BookmarkPlus     = wrap(PI.BookmarkSimple);
export const BookOpen         = wrap(PI.BookOpen);
export const StickyNote       = wrap(PI.Note);
export const Briefcase        = wrap(PI.Briefcase);

// ---- Actions ----
export const Plus         = wrap(PI.Plus, 'bold');
export const PlusCircle   = wrap(PI.PlusCircle);
export const Minus        = wrap(PI.Minus, 'bold');
export const X            = wrap(PI.X, 'bold');
export const Edit         = wrap(PI.PencilSimple, 'regular');
export const Edit2        = wrap(PI.PencilSimple, 'regular');
export const Edit3        = wrap(PI.PencilSimpleLine, 'regular');
export const Pencil       = wrap(PI.Pencil, 'regular');
export const Trash2       = wrap(PI.Trash);
export const Copy         = wrap(PI.Copy);
export const Save         = wrap(PI.FloppyDisk);
export const Download     = wrap(PI.Download);
export const Upload       = wrap(PI.Upload);
export const UploadCloud  = wrap(PI.CloudArrowUp);
export const Send         = wrap(PI.PaperPlaneRight);
export const Search       = wrap(PI.MagnifyingGlass, 'regular');
export const Filter       = wrap(PI.Funnel);
export const Settings     = wrap(PI.Gear);
export const Eye          = wrap(PI.Eye, 'regular');
export const EyeOff       = wrap(PI.EyeSlash, 'regular');
export const Lock         = wrap(PI.Lock);
export const Paperclip    = wrap(PI.Paperclip, 'regular');
export const Scissors     = wrap(PI.Scissors, 'regular');
export const Crop         = wrap(PI.Crop, 'regular');
export const GripVertical = wrap(PI.DotsSixVertical, 'bold');
export const MoreHorizontal = wrap(PI.DotsThree, 'bold');
export const MoreVertical   = wrap(PI.DotsThreeVertical, 'bold');

// ---- Comms ----
export const Mail           = wrap(PI.Envelope);
export const Phone          = wrap(PI.Phone);
export const MessageCircle  = wrap(PI.Chat);
export const MessageSquare  = wrap(PI.ChatCircle);
export const PaperPlaneTilt = wrap(PI.PaperPlaneTilt);
export const Smartphone     = wrap(PI.DeviceMobile);
export const Megaphone      = wrap(PI.Megaphone);

// ---- Money / commerce ----
export const DollarSign  = wrap(PI.CurrencyDollar);
export const CreditCard  = wrap(PI.CreditCard);
export const Cardholder  = wrap(PI.Cardholder);
export const Invoice     = wrap(PI.Invoice);
export const StripeLogo  = wrap(PI.StripeLogo);
export const Percent     = wrap(PI.Percent);
export const Tag         = wrap(PI.Tag);
export const Gift        = wrap(PI.Gift);
export const TrendingUp   = wrap(PI.TrendUp);
export const TrendingDown = wrap(PI.TrendDown);

// ---- Place / map ----
export const Map        = wrap(PI.MapTrifold);
export const MapPin     = wrap(PI.MapPin);
export const MapPinOff  = wrap(PI.MapPinLine, 'regular');
export const Locate     = wrap(PI.CrosshairSimple, 'regular');
export const Satellite  = wrap(PI.GlobeHemisphereWest);
export const Home       = wrap(PI.House);
export const Truck      = wrap(PI.Truck);

// ---- Time / weather ----
export const Calendar    = wrap(PI.Calendar);
export const CalendarOff = wrap(PI.CalendarX);
export const Clock       = wrap(PI.Clock);
export const Sun         = wrap(PI.Sun);
export const Moon        = wrap(PI.Moon);
export const CloudSun    = wrap(PI.CloudSun);
export const Wind        = wrap(PI.Wind);
export const Thermometer = wrap(PI.Thermometer);
export const Droplets    = wrap(PI.Drop);

// ---- Visual ----
export const Image       = wrap(PI.Image);
export const ImageIcon   = wrap(PI.Image);
export const ImagePlus   = wrap(PI.ImageSquare);
export const Camera      = wrap(PI.Camera);
export const Palette     = wrap(PI.Palette);
export const Sparkles    = wrap(PI.Sparkle);
export const Sparkle     = wrap(PI.Sparkle);
export const Scribble    = wrap(PI.Scribble);
export const TextT       = wrap(PI.TextT, 'regular');
export const Star        = wrap(PI.Star);
export const ShootingStar = wrap(PI.ShootingStar);

// ---- Misc UI ----
export const Circle      = wrap(PI.Circle);
export const Dot         = wrap(PI.Circle, 'fill');
export const Square      = wrap(PI.Square);
export const Hash        = wrap(PI.Hash, 'regular');
export const Link        = wrap(PI.Link, 'regular');
export const Link2       = wrap(PI.LinkSimple, 'regular');
export const ExternalLink = wrap(PI.ArrowSquareOut, 'regular');
export const PanelLeft   = wrap(PI.SidebarSimple);
export const Layers      = wrap(PI.Stack);
export const GitBranch   = wrap(PI.GitBranch, 'regular');
export const Plug        = wrap(PI.Plug);
export const Lightbulb   = wrap(PI.Lightbulb);
export const Ticket      = wrap(PI.Ticket);
export const Monitor     = wrap(PI.Monitor);
export const Desktop     = wrap(PI.Desktop);
export const QrCode      = wrap(PI.QrCode, 'regular');
export const Code        = wrap(PI.Code, 'regular');
export const Zap         = wrap(PI.Lightning);

// ---- Loader / spinners ----
export const Loader2 = wrap(PI.CircleNotch, 'bold');

// ---- Tools / domain ----
export const Wrench      = wrap(PI.Wrench);
export const Construction = wrap(PI.Hammer);
export const Calculator  = wrap(PI.Calculator);
export const Ruler       = wrap(PI.Ruler);
export const Package     = wrap(PI.Package);
export const Log         = wrap(PI.Log);
export const TreePine    = wrap(PI.Tree);
export const Trees       = wrap(PI.Tree);
export const Spline      = wrap(PI.Polygon, 'regular');
export const Waypoints   = wrap(PI.LineSegments, 'regular');

// ---- Missing-from-original-pass: activity & feedback ----
export const Activity         = wrap(PI.Pulse);
export const AlertOctagon     = wrap(PI.WarningOctagon);
export const Archive          = wrap(PI.Archive);
export const ArchiveRestore   = wrap(PI.Archive);
export const Inbox            = wrap(PI.Tray);
export const Bell             = wrap(PI.Bell);
export const BellRing         = wrap(PI.BellRinging);
export const Award            = wrap(PI.Medal);
export const Trophy           = wrap(PI.Trophy);
export const Gauge            = wrap(PI.Gauge);
export const Target           = wrap(PI.Target);
export const Timer            = wrap(PI.Timer);
export const CircleDashed     = wrap(PI.CircleDashed);
export const HelpCircle       = wrap(PI.Question);
export const ThumbsUp         = wrap(PI.ThumbsUp);
export const ThumbsDown       = wrap(PI.ThumbsDown);

// ---- More arrows ----
export const ArrowDownCircle    = wrap(PI.ArrowCircleDown, 'regular');
export const ArrowUpCircle      = wrap(PI.ArrowCircleUp, 'regular');
export const ArrowLeftRight     = wrap(PI.ArrowsLeftRight, 'regular');
export const ArrowRightLeft     = wrap(PI.ArrowsLeftRight, 'regular');
export const ArrowLeftToLine    = wrap(PI.ArrowLineLeft, 'regular');
export const ArrowRightFromLine = wrap(PI.ArrowRight, 'regular');
export const ChevronsLeft       = wrap(PI.CaretDoubleLeft, 'bold');
export const ChevronsRight      = wrap(PI.CaretDoubleRight, 'bold');
export const RotateCw           = wrap(PI.ArrowClockwise, 'regular');
export const FlipHorizontal     = wrap(PI.FlipHorizontal, 'regular');
export const FlipVertical       = wrap(PI.FlipVertical, 'regular');
export const Redo               = wrap(PI.ArrowArcRight, 'regular');
export const Reply              = wrap(PI.ArrowBendUpLeft, 'regular');
export const Undo2              = wrap(PI.ArrowUUpLeft, 'regular');
export const Navigation         = wrap(PI.NavigationArrow);

// ---- Calendar / clipboard ----
export const Calendar1        = wrap(PI.CalendarBlank);
export const CalendarClock    = wrap(PI.CalendarCheck);
export const CalendarDays     = wrap(PI.Calendar);
export const Clipboard        = wrap(PI.Clipboard);
export const ClipboardCheck   = wrap(PI.ClipboardText);
export const ClipboardEdit    = wrap(PI.ClipboardText);
export const ClipboardList    = wrap(PI.ClipboardText);
export const Pin              = wrap(PI.PushPin);
export const PinOff           = wrap(PI.PushPinSlash);

// ---- Status checks ----
export const CheckCheck       = wrap(PI.Checks, 'bold');
export const CheckSquare      = wrap(PI.CheckSquare);

// ---- Files & folders ----
export const File             = wrap(PI.File);
export const FilePlus         = wrap(PI.FilePlus);
export const FileSignature    = wrap(PI.FileText);
export const FolderArchive    = wrap(PI.FileZip);
export const BookUser         = wrap(PI.AddressBook);

// ---- Layout ----
export const LayoutDashboard  = wrap(PI.SquaresFour);
export const LayoutGrid       = wrap(PI.GridFour);
export const Grid3x3          = wrap(PI.GridFour);
export const Rows             = wrap(PI.Rows);
export const Columns          = wrap(PI.Columns);
export const Kanban           = wrap(PI.Kanban);
export const SquaresFour      = wrap(PI.SquaresFour);
export const Layers3          = wrap(PI.StackSimple);
export const PanelLeftOpen    = wrap(PI.SidebarSimple);
export const PanelLeftClose   = wrap(PI.SidebarSimple);
export const List             = wrap(PI.List);
export const ListChecks       = wrap(PI.ListChecks);
export const ListFilter       = wrap(PI.Funnel);
export const SortAsc          = wrap(PI.SortAscending);
export const SortDesc         = wrap(PI.SortDescending);

// ---- Pointers / cursors ----
export const MousePointer     = wrap(PI.Cursor, 'regular');
export const MousePointer2    = wrap(PI.Cursor, 'regular');
export const Crosshair        = wrap(PI.Crosshair, 'regular');
export const Hand             = wrap(PI.Hand);

// ---- Brand / socials ----
export const Instagram        = wrap(PI.InstagramLogo);
export const Facebook         = wrap(PI.FacebookLogo);
export const Twitter          = wrap(PI.XLogo);
export const Linkedin         = wrap(PI.LinkedinLogo);
export const Youtube          = wrap(PI.YoutubeLogo);
export const Music2           = wrap(PI.TiktokLogo);
export const Globe            = wrap(PI.GlobeHemisphereWest);

// ---- Mail variants ----
export const MailCheck        = wrap(PI.EnvelopeOpen);
export const MailPlus         = wrap(PI.Envelope);
export const MailX            = wrap(PI.Envelope);
export const AtSign           = wrap(PI.At);
export const MessageCirclePlus = wrap(PI.ChatCircle);

// ---- Map extras ----
export const MapPinned        = wrap(PI.MapPinLine);
export const Route            = wrap(PI.MapTrifold);
export const House            = wrap(PI.House);

// ---- People ----
export const UserCheck        = wrap(PI.UserCheck);
export const UserCircle       = wrap(PI.UserCircle);
export const UserPlus2        = wrap(PI.UserPlus);

// ---- Domain ----
export const Banknote         = wrap(PI.Money);
export const Landmark         = wrap(PI.Bank);
export const MoneyWavy        = wrap(PI.MoneyWavy);
export const Wallet           = wrap(PI.Wallet);
export const Receipt          = wrap(PI.Receipt);
export const ShoppingCart     = wrap(PI.ShoppingCart);
export const ShoppingBag      = wrap(PI.ShoppingBag);
export const Boxes            = wrap(PI.Cube);
export const Container        = wrap(PI.Package);
export const PackageMinus     = wrap(PI.Package);
export const PackagePlus      = wrap(PI.Package);
export const Fuel             = wrap(PI.GasPump);
export const Utensils         = wrap(PI.ForkKnife);

// ---- UI ----
export const BarChart3        = wrap(PI.ChartBar);
export const FadersHorizontal = wrap(PI.FadersHorizontal, 'regular');
export const Settings2        = wrap(PI.GearSix);
export const Share2           = wrap(PI.Share);
export const Smile            = wrap(PI.Smiley);
export const Play             = wrap(PI.Play, 'fill');
export const Printer          = wrap(PI.Printer);
export const Loader           = wrap(PI.Spinner, 'bold');
export const LogOut           = wrap(PI.SignOut);
export const Key              = wrap(PI.Key);
export const Keyboard         = wrap(PI.Keyboard);
export const Wand2            = wrap(PI.MagicWand);
export const Video            = wrap(PI.VideoCamera);
// eslint-disable-next-line no-shadow-restricted-names
export const Infinity         = wrap(PI.Infinity);
export const PenLine          = wrap(PI.PencilLine);
export const Link2Off         = wrap(PI.LinkBreak, 'regular');
export const ZapOff           = wrap(PI.LightningSlash);

// ---- Type alias mirroring lucide-react's `LucideIcon` export
export type Icon = typeof Briefcase;
export type LucideIcon = typeof Briefcase;
